/**
 * Композиция провайдеров и глобальных подписок.
 *
 * Здесь же «приземляются» коды ошибок, которые обрабатывает приложение
 * целиком, а не экран (таблица CLIENT_WEB.md §2.3).
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'

import { onGlobalApiError } from '@shared/api/client'
import { closeUserDb } from '@shared/db'
import { ToastProvider } from '@shared/ui'
import { onTabMessage, type TabMessage } from '@shared/ws/leader'
import { syncEngine } from '@shared/sync/instance'

import { useAppStore } from '../model/app-store'
import { watchSystemTheme } from '../model/theme'
import { startActiveChatMirror } from '../boot/active-chat'
import { startVisibleConversationsMirror } from '../boot/visible-conversations'
import { CHAT_SEARCH_ATTR, startHotkeys } from '../model/hotkeys'
import { router } from '../routes/router'
import { startServiceWorkerBridge } from '../boot/sw-bridge'
import {
  handleRemoteSessionEnd,
  handleRemoteSessionLost,
  handleRestrictedAccountState,
  handleSessionLost,
  handleTerminalAccountState,
  startPendingDeletionRecovery,
} from '../boot/session-lifecycle'
import {
  consumeInterruptedAuthTransition,
  consumeMissedAuthTransition,
  fenceAuthTransition,
  finishRemoteAuthTransition,
  onAuthTransitionStorageMessage,
  onInterruptedAuthTransitionReload,
} from '../boot/auth-transition'

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Экран читает данные из реплики; кэш запросов — только слой чтения
        // поверх неё (CC §4.1 п. 2), поэтому агрессивный refetch не нужен.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          // Повторяем только сетевые отказы: коды контракта решает обработчик.
          const isNetwork = error instanceof TypeError || error?.constructor?.name === 'NetworkError'
          return isNetwork && failureCount < 2
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  })
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)

  useGlobalErrorHandling()
  useNetworkState()
  useTabMessages()
  useForegroundReconnect()

  useEffect(() => watchSystemTheme(), [])
  // §8.4: маршруты из уведомлений и обновление клиента (SYS-04).
  useEffect(() => startServiceWorkerBridge(), [])
  // §8.5 п. 1–2: отметка «диалог открыт» живёт вместе с видимостью вкладки.
  useEffect(() => startActiveChatMirror(), [])
  // §6.7: presence-подписки следуют за строками в viewport C-01.
  useEffect(() => startVisibleConversationsMirror(), [])
  // Замороженная вкладка могла заблокировать deleteDatabase и пропустить BC.
  // При pageshow/возврате в foreground повторяем durable privacy-intent.
  useEffect(() => startPendingDeletionRecovery(), [])
  // §4.6: горячие клавиши desktop (D-02).
  useDesktopHotkeys()

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  )
}

/* ===================================================================== */
/* Глобальные коды ошибок (§2.3)                                          */
/* ===================================================================== */

function useGlobalErrorHandling(): void {
  useEffect(
    () =>
      onGlobalApiError((error, requestOwnerId) => {
        const store = useAppStore.getState()
        const currentOwnerId = store.me?.user.id ?? null
        const ownerScoped =
          error.code === 'session_expired' ||
          error.code === 'unauthenticated' ||
          error.code === 'account_restricted' ||
          error.code === 'account_suspended' ||
          error.code === 'account_pending_deletion' ||
          error.code === 'consent_required' ||
          error.code === 'onboarding_incomplete' ||
          error.code === 'step_up_required'
        // Поздний ответ запроса A не меняет lifecycle уже вошедшего B.
        // `null` тоже считается старой pre-auth epoch, если owner уже есть.
        if (ownerScoped && currentOwnerId && requestOwnerId !== currentOwnerId) return

        switch (error.code) {
          case 'session_expired':
          case 'unauthenticated':
            /*
             * 401 на старте — обычный путь первого запуска (сессии просто нет),
             * а не потеря сессии. SYS-07 показывается ТОЛЬКО когда сессия
             * была: иначе пользователь увидел бы «нужно войти снова», ни разу
             * не войдя.
             */
            if (store.phase === 'ready' || store.phase === 'onboarding') {
              void handleSessionLost(requestOwnerId ?? undefined)
            } else {
              store.setPhase('unauthenticated')
            }
            break

          case 'account_restricted':
            if (currentOwnerId) handleRestrictedAccountState(currentOwnerId)
            else store.setBlocking({ kind: 'account_restricted' })
            break

          case 'account_suspended':
            if (currentOwnerId) handleTerminalAccountState(currentOwnerId, 'account_suspended')
            else store.setBlocking({ kind: 'account_suspended' })
            break

          case 'account_pending_deletion':
            if (currentOwnerId) handleTerminalAccountState(currentOwnerId, 'account_pending_deletion')
            else store.setBlocking({ kind: 'account_pending_deletion' })
            break

          case 'consent_required':
            // Фоновые запросы и reconnect приостанавливаются до POST /me/consents.
            syncEngine()?.suspendForConsent?.()
            store.setBlocking({ kind: 'consent_required' })
            break

          case 'client_upgrade_required':
            store.setBlocking({ kind: 'client_upgrade_required' })
            void navigator.serviceWorker?.getRegistration().then((r) => r?.update())
            break

          case 'csrf_rejected':
            store.setBlocking({
              kind: 'diagnostic',
              code: error.code,
              requestId: error.requestId,
            })
            break

          case 'onboarding_incomplete':
            store.setPhase('onboarding')
            break

          default:
            break
        }
      }),
    [],
  )
}

/* ===================================================================== */
/* Сетевое состояние (CC §3.2)                                            */
/* ===================================================================== */

function useNetworkState(): void {
  useEffect(() => {
    const goOffline = (): void => useAppStore.getState().setOnline(false)
    const goOnline = (): void => {
      // `navigator.onLine` — лишь подсказка: подтверждением служит успешный
      // обмен с сервером (CC §3.2), и его подтверждает движок.
      syncEngine()?.confirmOnline()
    }

    globalThis.addEventListener('offline', goOffline)
    globalThis.addEventListener('online', goOnline)

    if (!navigator.onLine) goOffline()

    return () => {
      globalThis.removeEventListener('offline', goOffline)
      globalThis.removeEventListener('online', goOnline)
    }
  }, [])
}

/* ===================================================================== */
/* Сообщения между вкладками (§2.4 п. 4–5)                                */
/* ===================================================================== */

function useTabMessages(): void {
  useEffect(() => startTabLifecycle(), [])
}

/** Единый lifecycle listener; reload внедряется для детерминированной проверки. */
export function startTabLifecycle(reloadPage: () => void = () => globalThis.location.reload()): () => void {
  let activeAuthTransitionId: string | null = null
  let documentSuspended = false
  const suspendDocument = (): void => {
    documentSuspended = true
    // Safari keeps a navigated document in bfcache. Its WebSocket, queue and
    // IndexedDB connection must be closed before WebKit freezes the event
    // loop, otherwise the next document can hang behind an unfinished write.
    try {
      closeUserDb({ abortActiveTransactions: true })
    } finally {
      fenceAuthTransition()
    }
  }
  const reloadInterruptedSource = (): boolean => {
    if (!consumeInterruptedAuthTransition()) return false
    fenceAuthTransition()
    reloadPage()
    return true
  }
  const handleMessage = (message: TabMessage): void => {
    switch (message.type) {
      case 'auth_transition_started':
        if (activeAuthTransitionId === message.transition_id) break
        activeAuthTransitionId = message.transition_id
        fenceAuthTransition()
        useAppStore.getState().setOnline(false)
        break

      case 'auth_transition_complete':
        // Storage — durable запасной канал. Вкладка могла пропустить `started`
        // целиком во freeze/bfcache, поэтому lone terminal тоже authoritative.
        if (activeAuthTransitionId && activeAuthTransitionId !== message.transition_id) {
          // Однослотовый storage уже мог перезаписать complete X более новым
          // complete Y. Остаёмся fail-closed и сверяем cookie только после reload.
          fenceAuthTransition()
          reloadPage()
          break
        }
        if (!activeAuthTransitionId) fenceAuthTransition()
        activeAuthTransitionId = null
        finishRemoteAuthTransition()
        // Cookie origin-wide. После success, invalid OTP или сетевого
        // отказа заново определяем `/me`; это покрывает и first-login,
        // когда у соседней вкладки `me === null`.
        reloadPage()
        break

      case 'session_lost':
        handleRemoteSessionLost(message.user_id)
        break

      case 'logout':
        handleRemoteSessionEnd(message.user_id)
        break

      case 'owner_changed':
        // База удаляется намеренно: сначала отпускаем WS/leader/Dexie,
        // затем показываем вход, чтобы не блокировать удаление в соседней вкладке.
        handleRemoteSessionEnd(message.previous_user_id)
        break

      case 'force_reload':
        if (useAppStore.getState().me?.user.id === message.user_id) reloadPage()
        break

      default:
        break
    }
  }

  const recoverMissed = (): void => {
    if (document.visibilityState === 'hidden') return
    if (documentSuspended) {
      // A bfcache document cannot safely resume the replica it synchronously
      // fenced on pagehide/freeze. Recreate the session from cookie + durable
      // state in a fresh JavaScript epoch.
      reloadPage()
      return
    }
    // Source-document мог уйти в bfcache во время durable preflight.
    // pagehide/freeze синхронно закрывает его DB/traffic; продолжать этот
    // JavaScript context после pageshow нельзя даже при откате IDB-транзакции.
    if (reloadInterruptedSource()) return
    const message = consumeMissedAuthTransition()
    if (message) handleMessage(message)
  }
  const stopChannel = onTabMessage(handleMessage)
  const stopStorage = onAuthTransitionStorageMessage(handleMessage)
  const stopInterruptedReload = onInterruptedAuthTransitionReload(() => {
    reloadInterruptedSource()
  })
  document.addEventListener('freeze', suspendDocument)
  globalThis.addEventListener('pagehide', suspendDocument)
  globalThis.addEventListener('pageshow', recoverMissed)
  document.addEventListener('visibilitychange', recoverMissed)
  recoverMissed()

  return () => {
    stopChannel()
    stopStorage()
    stopInterruptedReload()
    document.removeEventListener('freeze', suspendDocument)
    globalThis.removeEventListener('pagehide', suspendDocument)
    globalThis.removeEventListener('pageshow', recoverMissed)
    document.removeEventListener('visibilitychange', recoverMissed)
  }
}

/* ===================================================================== */
/* Горячие клавиши desktop (§4.6, D-02)                                   */
/* ===================================================================== */

function useDesktopHotkeys(): void {
  useEffect(
    () =>
      startHotkeys({
        openShortcuts: () => useAppStore.getState().setShortcutsOpen(true),

        /*
         * `/` и `Ctrl/Cmd + K` адресуют поиск по списку диалогов (C-02).
         * Если список не открыт, сначала выполняется переход — действие
         * остаётся тем же, что и по нажатию кнопки поиска мышью.
         */
        focusChatSearch: () => {
          const focus = (): void => {
            const input = document.querySelector<HTMLElement>(`[${CHAT_SEARCH_ATTR}]`)
            if (input) {
              input.focus()
              return
            }
            // Поиск свёрнут — просим экран его открыть.
            document.querySelector<HTMLElement>('[data-hotkey-chat-search-toggle]')?.click()
            requestAnimationFrame(() => document.querySelector<HTMLElement>(`[${CHAT_SEARCH_ATTR}]`)?.focus())
          }

          if (!router.state.location.pathname.startsWith('/chats')) {
            void router.navigate({ to: '/chats' }).then(() => requestAnimationFrame(focus))
            return
          }

          focus()
        },
      }),
    [],
  )
}

/* ===================================================================== */
/* Возврат вкладки в foreground: PWA-05 (CC §4.5 п. 7)                    */
/* ===================================================================== */

function useForegroundReconnect(): void {
  useEffect(() => {
    const handler = (): void => {
      if (document.visibilityState !== 'visible') return
      syncEngine()?.reconnectNow()
    }

    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])
}
